import React, { useCallback, useMemo, useRef } from 'react';

import { ImportStrategyContext } from './ImportStrategyContext.tsx';
import { IMPORT_STRATEGIES } from './ImportStrategies/importStrategies.tsx';
import { ImportStrategyRegistry } from './ImportStrategies/ImportStrategyRegistry.tsx';
import type { OnboardingImportLauncherProps } from './OnboardingImportLauncherProps.ts';
import type { ImportTriggerType } from './types.ts';

/**
 * Placeholder passed as `children` to each strategy.
 *
 * Every strategy clones its `children` to attach `onItemClick`, so `children` must be a
 * valid element even when the strategy renders no visible card. This component swallows
 * the injected props and renders nothing.
 */
const HeadlessStrategyChild = (): null => null;

/**
 * Runs the standard import-from-sources flow outside the editor right rail.
 *
 * The editor rail (`SourcesList`) renders a card per strategy and lets the strategy own
 * the click target. Entry points such as the onboarding Import CTA in the space side
 * navigation already own their trigger — a dropdown item — and have no page context. This
 * component mounts the same strategies headlessly and exposes `startImport` so the caller
 * can start an import in place, with no navigation beforehand.
 *
 * There is no draft to import into here, so `contentId` is empty and `parentPageId` is
 * null: the upload pipeline creates its own destination page at the root of the space.
 * All modals (progress, errors, auth) are rendered by `ImportStrategyContext`, and the
 * user is only navigated to the imported page once the import succeeds.
 */
export const OnboardingImportLauncher = ({
	spaceKey,
	children,
}: OnboardingImportLauncherProps): React.JSX.Element => {
	const triggersRef = useRef<Record<string, ImportTriggerType>>({});

	// Stable per-strategy callbacks so the strategies' registration effects do not re-run
	// on every render of this component.
	const registerTriggers = useMemo(
		() =>
			Object.fromEntries(
				IMPORT_STRATEGIES.map(({ key }) => [
					key,
					(trigger: ImportTriggerType) => {
						triggersRef.current[key] = trigger;
					},
				]),
			),
		[],
	);

	const startImport = useCallback((source: string) => {
		triggersRef.current[source]?.start();
	}, []);

	const prefetchImport = useCallback((source: string) => {
		triggersRef.current[source]?.prefetch?.();
	}, []);
	return (
		<ImportStrategyRegistry
			surface="onboarding"
			contentId=""
			spaceId={null}
			hasSourceRestrictions={false}
		>
			{(importStrategies) => (
				<ImportStrategyContext
					contentId=""
					parentPageId={null}
					hasUserAddedContent={false}
					currentSpace={spaceKey}
					restrictionType="free"
					hasSourceRestrictions={false}
				>
					{({ handleStartStrategy, handleDocumentSelect, handleDocumentSelectError }) => (
						<>
							{importStrategies.map(({ key, component: ImportStrategyComponent }) => (
								<ImportStrategyComponent
									key={key}
									onStartStrategy={handleStartStrategy}
									onDocumentSelect={handleDocumentSelect}
									onDocumentSelectError={handleDocumentSelectError}
									registerTrigger={registerTriggers[key]}
									skipAuthInterstitial
								>
									<HeadlessStrategyChild />
								</ImportStrategyComponent>
							))}
							{children({ startImport, prefetchImport })}
						</>
					)}
				</ImportStrategyContext>
			)}
		</ImportStrategyRegistry>
	);
};
